import { useRef } from 'react';
import type { Stripe, StripeElements } from '@stripe/stripe-js';
import toast from 'react-hot-toast';
import {
  CreditCard,
  CreditCardService,
  Plan,
  SitterPlanService,
  StripePaymentServices,
  TermsAcceptanceService,
  User
} from 'us-web-services';
import { type TrackerService, TrackerTypes } from '../../services/TrackerService/TrackerService';

export interface UsePurchaseFlowParams {
  stripe: Stripe | null;
  elements: StripeElements | null;
  user: User;
  defaultPlan?: Plan;
  creditCard?: CreditCard;
  selectedPaymentMethodType?: string;
  total: string;
  returnUrl: string;
  executeRecaptcha: (action: string) => Promise<string>;
  tracker: Pick<TrackerService, 'track'>;
  t: (key: string) => string;
  setIsSubmitting: (value: boolean) => void;
  onCardSaved: (card: CreditCard) => void;
  onSuccess: () => void;
  onError: (error: unknown) => void;
}

export interface UsePurchaseFlowResult {
  submitPayment: () => Promise<void>;
}

const convertToCents = (amount: string): number => Math.round(parseFloat(amount) * 100);

export const usePurchaseFlow = (params: UsePurchaseFlowParams): UsePurchaseFlowResult => {
  const {
    stripe,
    elements,
    user,
    defaultPlan,
    creditCard,
    selectedPaymentMethodType,
    total,
    returnUrl,
    executeRecaptcha,
    tracker,
    t,
    setIsSubmitting,
    onCardSaved,
    onSuccess,
    onError
  } = params;

  const paymentIntentRef = useRef<{
    clientSecret: string;
    id: string;
    amount: number;
    paymentMethodType?: string;
  } | null>(null);
  const paymentMethodIdRef = useRef<string | null>(null);
  const chargeConfirmedRef = useRef(false);
  const paymentMethodSavedRef = useRef(false);
  const inFlightRef = useRef(false);

  const stopSubmitting = (): void => {
    inFlightRef.current = false;
    setIsSubmitting(false);
  };

  const createPaymentIntent = async (amount: number): Promise<string | undefined> => {
    try {
      const { data: { data: paymentData } } = await StripePaymentServices.createPaymentIntent({
        amount,
        currency: 'usd',
        userId: user.id,
        paymentMethodType: selectedPaymentMethodType
      });

      return paymentData.clientSecret;
    } catch {
      toast.error(t('pages.sitter.buyBackgroundCheck.messages.initializingPayment'));
      return undefined;
    }
  };

  const ensurePaymentIntent = async (amount: number): Promise<{ clientSecret: string; id: string } | undefined> => {
    const cached = paymentIntentRef.current;
    if (cached && cached.amount === amount && cached.paymentMethodType === selectedPaymentMethodType) {
      return cached;
    }

    const clientSecret = await createPaymentIntent(amount);
    if (!clientSecret) {
      return undefined;
    }

    paymentIntentRef.current = {
      clientSecret,
      id: clientSecret.split('_secret_')[0],
      amount,
      paymentMethodType: selectedPaymentMethodType
    };
    return paymentIntentRef.current;
  };

  const saveCardPaymentMethod = async (paymentMethodId: string): Promise<boolean> => {
    const recaptchaToken = await executeRecaptcha('creditcard');

    const response = await CreditCardService.create({
      paymentToken: paymentMethodId,
      userId: user.id,
      recaptchaToken
    });

    if (!response.data) {
      return false;
    }

    onCardSaved(response.data);

    await StripePaymentServices.updateCustomerDefaultPaymentMethod({
      paymentMethodId,
      userId: user.id
    });

    return true;
  };

  const storeTermsAcceptance = async (planId: number): Promise<void> => {
    await TermsAcceptanceService.create({
      userId: user.id,
      planId,
      terms: { alias: 'tos' },
      accepted: Date.now()
    });
  };

  const createPlan = async (paymentIntentId = ''): Promise<void> => {
    try {
      if (!defaultPlan?.id) {
        stopSubmitting();
        return;
      }

      await storeTermsAcceptance(defaultPlan.id);

      await SitterPlanService.create({
        paymentIntentId,
        planId: defaultPlan.id,
        userId: user.id
      });

      tracker.track('background-check-purchase-success', {}, 'sitter-background-check');
      tracker.track('sitter-plan-purchased', {
        event: 'sitter-plan-purchased',
        plan: 'Basic Sitter Membership',
        uid: user.id
      }, undefined, {
        services: [TrackerTypes.GA]
      });

      onSuccess();
    } catch (error) {
      stopSubmitting();
      tracker.track('background-check-purchase-failure', {}, 'sitter-background-check');
      onError(error);
    }
  };

  const confirmCharge = async (): Promise<boolean> => {
    if (!stripe || !elements) {
      return false;
    }

    const { error: submitError } = await elements.submit();
    if (submitError) {
      toast.error(submitError.message || t('pages.sitter.buyBackgroundCheck.messages.errorWithPaymentForm'));
      return false;
    }

    const amount = convertToCents(total);
    const paymentIntent = await ensurePaymentIntent(amount);
    if (!paymentIntent) {
      return false;
    }

    const { error, paymentIntent: confirmedIntent } = await stripe.confirmPayment({
      elements,
      clientSecret: paymentIntent.clientSecret,
      confirmParams: {
        return_url: returnUrl,
        payment_method_data: {
          billing_details: {
            name: `${user.firstName} ${user.lastName}`,
            email: user.email
          }
        }
      },
      redirect: 'if_required'
    });

    if (error) {
      toast.error(error.message || t('pages.sitter.buyBackgroundCheck.messages.errorWithPayment'));
      return false;
    }

    if (!confirmedIntent || !confirmedIntent.payment_method) {
      toast.error(t('pages.sitter.buyBackgroundCheck.messages.noPaymentMethodReceived'));
      return false;
    }

    paymentIntentRef.current = {
      clientSecret: paymentIntent.clientSecret,
      id: confirmedIntent.id,
      amount,
      paymentMethodType: selectedPaymentMethodType
    };
    paymentMethodIdRef.current = typeof confirmedIntent.payment_method === 'string'
      ? confirmedIntent.payment_method
      : confirmedIntent.payment_method.id;
    chargeConfirmedRef.current = true;
    return true;
  };

  const savePaymentMethod = async (): Promise<boolean> => {
    const paymentMethodId = paymentMethodIdRef.current;
    if (!paymentMethodId) {
      toast.error(t('pages.sitter.buyBackgroundCheck.messages.noPaymentMethodReceived'));
      return false;
    }

    if (selectedPaymentMethodType === 'card') {
      try {
        const saved = await saveCardPaymentMethod(paymentMethodId);
        if (!saved) {
          toast.error(t('pages.sitter.buyBackgroundCheck.messages.noCardReceived'));
          return false;
        }
      } catch {
        toast.error(t('pages.sitter.buyBackgroundCheck.messages.errorSavingCard'));
        return false;
      }
    } else {
      try {
        await StripePaymentServices.updateCustomerDefaultPaymentMethod({
          paymentMethodId,
          userId: user.id
        });
      } catch {
        toast.error(t('pages.sitter.buyBackgroundCheck.messages.errorProcessingPayment'));
        return false;
      }
    }

    paymentMethodSavedRef.current = true;
    return true;
  };

  const submitPayment = async (): Promise<void> => {
    if (inFlightRef.current || !stripe || !elements) {
      return;
    }
    inFlightRef.current = true;
    setIsSubmitting(true);

    if (creditCard?.id || total === '0.00') {
      await createPlan(paymentIntentRef.current?.id);
      return;
    }

    try {
      if (!chargeConfirmedRef.current && !(await confirmCharge())) {
        stopSubmitting();
        return;
      }

      if (!paymentMethodSavedRef.current && !(await savePaymentMethod())) {
        stopSubmitting();
        return;
      }

      await createPlan(paymentIntentRef.current?.id);
    } catch {
      toast.error(t('pages.sitter.buyBackgroundCheck.messages.errorProcessingPayment'));
      stopSubmitting();
    }
  };

  return { submitPayment };
};
